import { useCallback, useEffect, useState } from 'react';
import { GetPromotionContents } from 'src/graphql/queries/hygraph/PromotionContentQuery';
import { AnalyticsEvent, useAnalytics } from '@analytics';
import { datadogRum } from '@datadog/browser-rum';
import {
  CalculateRebatesQueryVariables,
  CuratedArticlesQuery,
  CuratedArticlesResponse,
  FaqsQuery,
  FaqsResponse,
  GetPackageForOpportunityQueryVariables,
  PackageQuote,
  PromotionContentsResponse,
  ProposalDownloadLinkQuery,
  ProposalDownloadLinkQueryVariables,
  QuoteByTrackingIdOpportunity,
  QuoteByTrackingIdQueryVariables,
  QuotePrepopValuesByTrackingIdQueryVariables,
  QuoteRequestsQueryVariables,
  QuoteStatus,
  SelectQuoteForOpportunityResponse,
  VendorsAwaitingQuotesQueryVariables,
  hygraphRequest,
  useCalculateRebatesQuery,
  useGetPackageForOpportunityQuery,
  useProposalDownloadLinkQuery,
  useQuoteByTrackingIdQuery,
  useQuotePrepopValuesByTrackingIdQuery,
  useQuoteRequestsQuery,
  useSelectQuoteForOpportunityMutation,
  useVendorsAwaitingQuotesQuery,
} from '@graphql';
import { usePackageContext } from '@provider';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { PACKAGE_CATEGORY_SELECTION_MODE } from '@utils/constants';
import { accessibleQuote } from '@utils/helper';
import { transformSelectQuoteForOpportunityInput } from './helper';

export interface PackageData {
  requiredCategories: { name: string; slug: string }[];
  quotesByCategory: {
    name: string;
    slug: string;
    quotes: PackageQuote[];
  }[];
  preselectedQuotes: PackageQuote[];
  preselectedVendor: string;
  isPackageComplete: boolean;
  opportunity: QuoteByTrackingIdOpportunity;
  flattenedQuotes: PackageQuote[];
}

export function useDatadogAddError() {
  const { packageForm } = usePackageContext();

  const logError = useCallback(
    (error: Error) => {
      const errorMessage = typeof error === 'string' ? error : error?.message;
      if (errorMessage) {
        const logMessage = `${packageForm.financialProduct?.id ?? ''} error: ${errorMessage}`;
        datadogRum.addError(new Error(logMessage));
      }
    },
    [packageForm.financialProduct?.id],
  );

  return {
    logError,
  };
}

export function useCalculateRebates(
  variables: CalculateRebatesQueryVariables,
  options?: Pick<UseQueryOptions, 'enabled'>,
) {
  const { isLoading, data, error } = useCalculateRebatesQuery(variables, {
    ...options,
    select: data => data.calculateRebates,
  });

  return {
    data,
    loading: isLoading,
    error,
  };
}

const TERMINAL_QUOTE_STATUSES = [
  QuoteStatus.ACCEPTED,
  QuoteStatus.COMPLETED,
  QuoteStatus.CANCELLED,
  QuoteStatus.EXPIRED,
  QuoteStatus.DECLINED,
];

export function usePrepop(variables: QuotePrepopValuesByTrackingIdQueryVariables) {
  const { isLoading, data, error } = useQuotePrepopValuesByTrackingIdQuery(variables, {
    select: data => data.quotePrepopValuesByTrackingId,
    enabled: variables.trackingId.trim() !== '',
    refetchInterval: data => {
      if (!data) return false;
      return TERMINAL_QUOTE_STATUSES.includes(data.status as QuoteStatus) ? false : 3000;
    },
  });

  return {
    data,
    loading: isLoading,
    error,
  };
}

export function useQuoteByTrackingId(variables: QuoteByTrackingIdQueryVariables) {
  const { setPackageForm } = usePackageContext();

  const [packageData, setPackageData] = useState<PackageData>({
    requiredCategories: [],
    quotesByCategory: [],
    preselectedQuotes: [],
    preselectedVendor: '',
    isPackageComplete: false,
    opportunity: {} as QuoteByTrackingIdOpportunity,
    flattenedQuotes: [],
  });

  const {
    data: quotesData,
    isLoading,
    error,
  } = useQuoteByTrackingIdQuery(variables, { enabled: true, useErrorBoundary: true });

  useEffect(() => {
    if (quotesData) {
      const { quoteByTrackingId: mainQuote } = quotesData;
      const { opportunity, financialProduct, productConfiguration, address, relatedQuotes } = mainQuote;

      if (!opportunity?.id || !relatedQuotes) {
        return;
      }

      // Copy financialProduct from mainQuote to relatedQuotes
      const updatedRelatedQuotes = relatedQuotes.map(quote => ({
        ...quote,
        financialProduct,
        productConfiguration,
      }));

      const flattenedQuotes = [mainQuote, ...updatedRelatedQuotes].filter(quote => accessibleQuote(quote.status));

      const requiredCategories = [...new Set(opportunity.categories?.map(category => category))];

      const preselectedQuoteIds = mainQuote?.selectedPackage?.trackingIds ?? [];
      const preselectedQuotes = flattenedQuotes.filter(quote =>
        preselectedQuoteIds.includes(quote.trackingId ?? ''),
      ) as PackageQuote[];

      const joinedCategories = [...flattenedQuotes.map(quote => quote.category), ...requiredCategories.map(cat => cat)];
      const categoryList = joinedCategories.filter(
        (category, index, self) => index === self.findIndex(cat => cat.slug === category.slug),
      );

      const quotesByCategory = categoryList.map(category => ({
        ...category,
        quotes: flattenedQuotes.filter(quote => quote.category?.name === category.name) as PackageQuote[],
      }));

      setPackageForm({
        opportunityId: opportunity.id,
        financialProduct,
        productConfiguration,
        address,
        enterprisePartnerId: mainQuote.enterprisePartnerId ?? '',
      });

      setPackageData({
        requiredCategories,
        quotesByCategory,
        preselectedQuotes,
        preselectedVendor: '',
        isPackageComplete:
          PACKAGE_CATEGORY_SELECTION_MODE[financialProduct.id] === 'all'
            ? requiredCategories.every(category => categoryList.find(cat => cat.slug === category.slug))
            : true,
        opportunity,
        flattenedQuotes: flattenedQuotes as PackageQuote[],
      });
    }
  }, [quotesData, setPackageForm]);

  return {
    data: packageData,
    loading: isLoading,
    error,
  };
}

export function useVendorsAwaitingQuotes(variables: VendorsAwaitingQuotesQueryVariables) {
  const { isLoading, data, error } = useVendorsAwaitingQuotesQuery(variables, {
    select: data => data.quoteByTrackingId.opportunity?.vendorsAwaitingQuotes,
  });

  return {
    data,
    loading: isLoading,
    error,
  };
}

export function useSelectQuoteForOpportunity({
  selectedQuotes,
  isCashPayment = false,
  handlePackageSubmission,
}: {
  selectedQuotes: PackageQuote[];
  isCashPayment?: boolean;
  handlePackageSubmission: (data: SelectQuoteForOpportunityResponse) => void;
}) {
  const { trackSubmissionEvents } = useAnalytics();

  const {
    mutate: selectQuoteForOpportunity,
    isLoading,
    isSuccess,
  } = useSelectQuoteForOpportunityMutation({
    useErrorBoundary: true,
    onSuccess: ({ selectQuoteForOpportunity }) => {
      trackSubmissionEvents({
        eventName: AnalyticsEvent.SubmittedCustomerPackage,
        extra: {
          category_count: selectedQuotes.length,
          cash_payment: isCashPayment,
        },
      });

      trackSubmissionEvents({
        eventName: AnalyticsEvent.SubmittedCustomerPackageCategory,
        quotes: selectedQuotes,
      });

      if (selectQuoteForOpportunity.packageId) {
        handlePackageSubmission(selectQuoteForOpportunity);
      }
    },
  });

  const selectQuotes = () => {
    const applicationInput = transformSelectQuoteForOpportunityInput({
      selectedQuotes,
      isCashPayment,
    });

    if (applicationInput) {
      selectQuoteForOpportunity(applicationInput);
    }
  };

  return {
    selectQuotes,
    isLoading,
    isSuccess,
  };
}

export function useProposalDownloadLink(
  variables: ProposalDownloadLinkQueryVariables,
  options?: UseQueryOptions<ProposalDownloadLinkQuery, string, string>,
) {
  return useProposalDownloadLinkQuery(variables, {
    enabled: !!variables.proposalId,
    select: data => data.proposalDownloadLink,
    ...options,
  });
}

export function useGetPackageForOpportunity(
  variables: GetPackageForOpportunityQueryVariables,
  options?: Pick<UseQueryOptions, 'enabled'>,
) {
  const { isLoading, data, error } = useGetPackageForOpportunityQuery(variables, {
    select: data => data.getPackageForOpportunity,
    ...options,
  });

  return {
    isLoading,
    data,
    error,
  };
}

export function useCuratedArticlesQuery(slug: string) {
  const { data, isLoading } = useQuery<CuratedArticlesResponse, unknown, CuratedArticlesResponse['values']>({
    queryKey: ['curated-articles', slug],
    queryFn: async () => {
      const client = hygraphRequest();
      const filter = {
        where: {
          slug: `${slug}-articles`,
        },
        first: 5,
      };
      return client.request(CuratedArticlesQuery, filter);
    },
    select: data => data?.values,
    enabled: !!slug,
  });

  return {
    data,
    isLoading,
  };
}

export function useFaqsQuery(slug: string) {
  const { data, isLoading } = useQuery<FaqsResponse, unknown, FaqsResponse['values']>({
    queryKey: ['faqs', slug],
    queryFn: async () => {
      const client = hygraphRequest();
      const filter = {
        where: {
          slug,
        },
        first: 5,
      };
      return client.request(FaqsQuery, filter);
    },
    select: data => data?.values,
    enabled: !!slug,
  });

  return {
    data,
    isLoading,
  };
}

export function usePromotionContent({ promoCodes, partnerId }: { promoCodes: string[]; partnerId: string }) {
  const { data, isLoading } = useQuery<PromotionContentsResponse, unknown, PromotionContentsResponse['promotions']>({
    queryKey: ['promotion-content', partnerId, promoCodes],
    queryFn: async () => {
      const client = hygraphRequest();
      const filter = {
        partner: partnerId,
        promotionCodes: promoCodes,
      };
      return client.request(GetPromotionContents, filter);
    },
    select: data => data?.promotions,
  });

  return {
    data,
    isLoading,
  };
}

export const useQuoteRequests = (variables?: QuoteRequestsQueryVariables) => {
  const { isLoading, data, error } = useQuoteRequestsQuery(variables, {
    select: data => data.quoteRequests,
  });

  return {
    isLoading,
    data,
    error,
  };
};
